import React, { useState, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ToastContainer, toast } from "react-toastify";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";
import FullPageLoader from "../components/FullPageLoader";
import KakaoLoginButton from "../components/KakaoLoginButton";
import api from "../services/api";
import "react-toastify/dist/ReactToastify.css";

const S = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=DM+Sans:wght@400;500;600&display=swap');

.login-page {
  min-height: 100vh;
  background: #f9f7f4;
  display: flex;
  align-items: center;
  justify-content: center;f
  font-family: 'DM Sans', sans-serif;
  padding: 24px 16px;
  position: relative;
  overflow: hidden;
}

/* background blobs */
.login-blob {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;
}
.login-blob-1 {
  width: 420px; height: 420px;
  background: radial-gradient(circle, rgba(26,92,56,0.08) 0%, transparent 70%);
  top: -100px; left: -100px;
}
.login-blob-2 {
  width: 320px; height: 320px;
  background: radial-gradient(circle, rgba(26,92,56,0.06) 0%, transparent 70%);
  bottom: -80px; right: -80px;
}

.login-card {
  background: #fff;
  border-radius: 24px;
  border: 1px solid #e8e4df;
  box-shadow: 0 8px 40px rgba(0,0,0,0.08);
  padding: 44px 40px;
  width: 100%;
  max-width: 420px;
  position: relative;
  z-index: 1;
}

.login-logo {
  display: flex;
  justify-content: center;
  margin-bottom: 8px;
}
.login-logo img {
  height: 44px;
  object-fit: contain;
}

.login-title {
  font-family: 'Playfair Display', serif;
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a1a1a;
  text-align: center;
  margin-bottom: 6px;
}
.login-subtitle {
  font-size: 0.88rem;
  color: #8a8a8a;
  text-align: center;
  margin-bottom: 28px;
}

.lf-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #1a5c38;
  margin-bottom: 6px;
}
.lf-input-wrap {
  position: relative;
  margin-bottom: 16px;
}
.lf-input {
  width: 100%;
  border: 1.5px solid #e8e4df;
  border-radius: 11px;
  padding: 11px 14px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.92rem;
  color: #1a1a1a;
  background: #fff;
  outline: none;
  transition: border-color 0.18s;
  box-sizing: border-box;
}
.lf-input:focus { border-color: #1a5c38; }
.lf-input::placeholder { color: #bbb; }
.lf-input.has-icon { padding-right: 44px; }

.lf-eye {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #aaa;
  font-size: 0.95rem;
  transition: color 0.15s;
  background: none;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
}
.lf-eye:hover { color: #1a5c38; }

.lf-error {
  background: #fef2f2;
  border: 1px solid #fcd5d5;
  border-radius: 9px;
  padding: 10px 14px;
  font-size: 0.84rem;
  color: #d94f3d;
  margin-bottom: 14px;
}

.lf-forgot {
  display: block;
  text-align: right;
  font-size: 0.82rem;
  color: #1a5c38;
  text-decoration: none;
  margin-top: -8px;
  margin-bottom: 20px;
  font-weight: 500;
  transition: opacity 0.15s;
}
.lf-forgot:hover { opacity: 0.75; color: #1a5c38; }

.lf-submit {
  width: 100%;
  background: #1a5c38;
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 14px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.96rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.18s, transform 0.14s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.lf-submit:hover:not(:disabled) { background: #134429; transform: translateY(-1px); }
.lf-submit:disabled { background: #a0c4b0; cursor: not-allowed; }

.lf-spinner {
  width: 18px; height: 18px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: lfspin 0.7s linear infinite;
  flex-shrink: 0;
}
@keyframes lfspin { to { transform: rotate(360deg); } }

.lf-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 22px 0;
  color: #ccc;
  font-size: 0.8rem;
}
.lf-divider::before,
.lf-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e8e4df;
}

.lf-register-row {
  text-align: center;
  font-size: 0.88rem;
  color: #8a8a8a;
  margin-top: 20px;
}
.lf-register-row a {
  color: #1a5c38;
  font-weight: 600;
  text-decoration: none;
  transition: opacity 0.15s;
}
.lf-register-row a:hover { opacity: 0.75; }

@media (max-width: 480px) {
  .login-card { padding: 32px 22px; border-radius: 18px; }
  .login-title { font-size: 1.5rem; }
}
`;

const LoginPage = () => {
  const [email,        setEmail]        = useState("");
  const [password,     setPassword]     = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error,        setError]        = useState("");
  const [loading,      setLoading]      = useState(false);

  const { login }  = useContext(AuthContext);
  const navigate   = useNavigate();
  const location   = useLocation();
  const { t }      = useTranslation();
  const from       = location.state?.from?.pathname || "/";

  React.useEffect(() => {
    if (location.state?.message) toast.info(location.state.message);
    // Friendly notice when redirected here by an expired session (see api.js).
    if (new URLSearchParams(location.search).get("session") === "expired") {
      toast.info(t("auth.sessionExpired"));
    }
  }, [location.state, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await api.post("/users/login", { email: email.trim(), password });
      const { token, user } = response.data;
      localStorage.setItem("authToken", token);
      localStorage.setItem("userInfo", JSON.stringify(user));
      login(user, token);
      toast.success(t("auth.loginSuccess"));
      setTimeout(() => navigate(from, { replace: true }), 1500);
    } catch (err) {
      const msg = err.response?.data?.message || t("auth.loginFailed");
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{S}</style>
      {loading && <FullPageLoader text={t("auth.authenticating")} />}
      <div className="login-page">
        <div className="login-blob login-blob-1" />
        <div className="login-blob login-blob-2" />

        <div className="login-card">
          {/* Logo */}
          <div className="login-logo">
            <img src="/images/Logo1.png" alt="FMart" />
          </div>

          <h1 className="login-title">{t("auth.welcomeBack")}</h1>
          <p className="login-subtitle">{t("auth.signInSubtitle")}</p>

          <form onSubmit={handleLogin}>
            {/* Email */}
            <span className="lf-label">{t("auth.email")}</span>
            <div className="lf-input-wrap">
              <input
                className="lf-input"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            {/* Password */}
            <span className="lf-label">{t("auth.password")}</span>
            <div className="lf-input-wrap">
              <input
                className="lf-input has-icon"
                type={showPassword ? "text" : "password"}
                placeholder={t("auth.passwordPlaceholder")}
                value={password}
                onChange={e => setPassword(e.target.value)}
                disabled={loading}
                required
              />
              <button type="button" className="lf-eye" onClick={() => setShowPassword(v => !v)}>
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            {/* Forgot password */}
            <Link to="/forgot-password" className="lf-forgot">{t("auth.forgotPassword")}</Link>

            {/* Error */}
            {error && <div className="lf-error">{error}</div>}

            {/* Submit */}
            <button type="submit" className="lf-submit" disabled={loading}>
              {loading ? <><div className="lf-spinner" /> {t("auth.signingIn")}</> : t("auth.signIn")}
            </button>
          </form>

          {/* Renders nothing until the Kakao keys are configured on the backend. */}
          <KakaoLoginButton next={from} />

          <div className="lf-divider">{t("auth.or")}</div>

          <div className="lf-register-row">
            {t("auth.noAccount")}{" "}
            <Link to="/register">{t("auth.registerNow")}</Link>
          </div>
        </div>

        <ToastContainer position="top-right" autoClose={3000} />
      </div>
    </>
  );
};

export default LoginPage;